/** Session key for inbox notifications clicked this session (hide dot, keep in unread section). */
export const INBOX_ACKNOWLEDGED_NOTIFICATION_IDS_KEY =
  'mani:inboxAcknowledgedNotificationIds';

/**
 * Reads notification ids acknowledged in the current inbox session.
 */
export function readAcknowledgedInboxNotificationIds(): Set<number> {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const raw = sessionStorage.getItem(INBOX_ACKNOWLEDGED_NOTIFICATION_IDS_KEY);
    if (!raw) {
      return new Set();
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return new Set();
    }

    return new Set(
      parsed
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value)),
    );
  } catch {
    return new Set();
  }
}

/**
 * Persists acknowledged inbox notification ids for the current session.
 */
export function writeAcknowledgedInboxNotificationIds(ids: ReadonlySet<number>): void {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(
    INBOX_ACKNOWLEDGED_NOTIFICATION_IDS_KEY,
    JSON.stringify([...ids]),
  );
}

/**
 * Clears session acknowledgments (e.g. when the user refreshes the inbox).
 */
export function clearAcknowledgedInboxNotificationIds(): void {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem(INBOX_ACKNOWLEDGED_NOTIFICATION_IDS_KEY);
}

/**
 * Marks a notification as acknowledged in session storage and returns the updated set.
 */
export function acknowledgeInboxNotificationId(notificationId: number): Set<number> {
  const next = readAcknowledgedInboxNotificationIds();
  next.add(notificationId);
  writeAcknowledgedInboxNotificationIds(next);
  return next;
}
